import React, { useEffect, useMemo, useState } from 'react';
import type { CalendarPayload, DayData, SourceKey } from './types';
import { MonthDayCell } from './MonthDayCell';
import { WeekDayCell } from './WeekDayCell';

interface Props {
  payload: CalendarPayload | undefined;
  view: 'month' | 'week';
  enabledSources: SourceKey[];
  fromDate: string;
  toDate: string;
  today: string;
  focusedDate: string | null;
  onSelectDay: (dateIso: string) => void;
}

const EMPTY_DAY: DayData = {
  recovery: { sleep: null, resting_hr: null, hrv: null, readiness: null },
  training: [], assessment: [],
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Cell {
  dateStr: string;
  isCurrentMonth: boolean;
}

function pad2(n: number): string { return n < 10 ? `0${n}` : String(n); }
function toDateStr(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function monthCells(anchor: Date): Cell[] {
  const y = anchor.getUTCFullYear();
  const m = anchor.getUTCMonth();
  const first = new Date(Date.UTC(y, m, 1));
  const last = new Date(Date.UTC(y, m + 1, 0));
  const startDow = first.getUTCDay();
  const cells: Cell[] = [];

  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m, -i));
    cells.push({ dateStr: toDateStr(d), isCurrentMonth: false });
  }
  for (let d = 1; d <= last.getUTCDate(); d++) {
    cells.push({ dateStr: toDateStr(new Date(Date.UTC(y, m, d))), isCurrentMonth: true });
  }
  while (cells.length < 42) {
    const tail = cells[cells.length - 1].dateStr;
    const parts = tail.split('-').map(Number);
    const next = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + 1));
    cells.push({ dateStr: toDateStr(next), isCurrentMonth: false });
  }
  return cells;
}

function weekCells(anchor: Date): Cell[] {
  const start = new Date(anchor);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const cells: Cell[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    cells.push({ dateStr: toDateStr(d), isCurrentMonth: true });
  }
  return cells;
}

export function CalendarGrid({
  payload, view, enabledSources, fromDate, today, focusedDate, onSelectDay,
}: Props) {
  const anchor = useMemo(() => new Date(`${fromDate}T00:00:00Z`), [fromDate]);
  const cells = view === 'month' ? monthCells(anchor) : weekCells(anchor);
  const days = payload?.days ?? {};

  // Animate fade + 8px slide on view/anchor change (--motion-month-*).
  const transitionKey = `${view}:${fromDate}`;
  const [animKey, setAnimKey] = useState(transitionKey);
  const [animPhase, setAnimPhase] = useState<'in' | 'out'>('in');
  useEffect(() => {
    if (transitionKey === animKey) return;
    setAnimPhase('out');
    const fade = setTimeout(() => { setAnimKey(transitionKey); setAnimPhase('in'); }, 120);
    return () => clearTimeout(fade);
  }, [transitionKey, animKey]);

  const transitionStyle: React.CSSProperties = {
    transition: 'opacity var(--motion-month-duration) var(--motion-month-easing)',
    opacity: animPhase === 'in' ? 1 : 0,
  };

  return (
    <div className="flex flex-col h-full cgm-bg-primary" style={transitionStyle} data-tour="calendar-grid">
      <div className="grid grid-cols-7 border-x border-b border-[var(--card-border)] flex-shrink-0">
        {WEEKDAY_LABELS.map((d) => (
          <div key={d} className="py-1 text-center text-[11px] font-medium cgm-text-secondary">
            {d}
          </div>
        ))}
      </div>
      <div
        className="grid grid-cols-7 flex-1 min-h-0 border-x border-b border-[var(--card-border)]"
        style={{ gridTemplateRows: view === 'month' ? 'repeat(6, minmax(0, 1fr))' : '1fr' }}
      >
        {cells.map((c) => {
          const data = days[c.dateStr] ?? EMPTY_DAY;
          const isToday = today === c.dateStr;
          const focused = focusedDate === c.dateStr;
          return view === 'month'
            ? (
              <MonthDayCell
                key={c.dateStr}
                date={c.dateStr}
                day={data}
                enabledSources={enabledSources}
                isToday={isToday}
                focused={focused}
                isCurrentMonth={c.isCurrentMonth}
                onClick={() => onSelectDay(c.dateStr)}
              />
            )
            : (
              <WeekDayCell
                key={c.dateStr}
                date={c.dateStr}
                day={data}
                enabledSources={enabledSources}
                isToday={isToday}
                focused={focused}
                onClick={() => onSelectDay(c.dateStr)}
              />
            );
        })}
      </div>
    </div>
  );
}
